import { useEffect, useState } from "react";
import { api, currency } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from "@/components/ui/table";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { Search, Ban, Pencil, Eye, History } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import SaleTransactionDetail from "@/components/SaleTransactionDetail";

export default function Sales() {
  const { user } = useAuth();
  const canModify = user?.role === "admin" || user?.role === "manager";
  const isMgmt = ["platform_owner", "vendor_owner", "admin", "manager"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [payment, setPayment] = useState("All");
  const [status, setStatus] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [detail, setDetail] = useState(null);
  const [audit, setAudit] = useState([]);
  const [voiding, setVoiding] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [editing, setEditing] = useState(null);
  const [editLines, setEditLines] = useState([]);
  const [editReason, setEditReason] = useState("");

  const load = () => api.get("/sales", { params: {
    limit: 500,
    q: q || undefined,
    payment_method: payment === "All" ? undefined : payment,
    status: status === "All" ? undefined : status,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  }}).then(r => setRows(r.data));

  useEffect(() => { load(); }, [q, payment, status, dateFrom, dateTo]);

  const openDetail = async (s) => {
    setDetail(s);
    try {
      const r = await api.get(`/sales/${s.id}/audit`);
      setAudit(r.data);
    } catch { setAudit([]); }
  };

  const openVoid = (s) => { setVoiding(s); setVoidReason(""); };
  const submitVoid = async () => {
    try {
      await api.post(`/sales/${voiding.id}/void`, { reason: voidReason || "void" });
      toast.success("Voided"); setVoiding(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Void failed"); }
  };

  const openEdit = (s) => {
    setEditing(s);
    setEditLines(s.lines.map(l => ({ ...l })));
    setEditReason("");
  };
  const submitEdit = async () => {
    try {
      await api.post(`/sales/${editing.id}/edit`, {
        lines: editLines.map(l => ({ product_id: l.product_id, quantity: Number(l.quantity) })),
        reason: editReason || "edit",
      });
      toast.success("Sale edited"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Edit failed"); }
  };

  const totalFiltered = rows.reduce((s, r) => s + (r.status === "completed" ? r.total : 0), 0);

  return (
    <div className="p-6 md:p-10">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground mb-2">Sales</div>
          <h1 className="font-display text-4xl tracking-tight" data-testid="sales-title">Sales History</h1>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Filtered total</div>
          <div className="font-display text-2xl tabular-nums" data-testid="sales-total-filtered">{currency(totalFiltered)}</div>
        </div>
      </div>

      <Card className="p-4 mb-4 border-border shadow-sm">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="col-span-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <Input data-testid="sales-search" className="pl-9" placeholder="Search receipt or cashier…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Select value={payment} onValueChange={setPayment}>
            <SelectTrigger data-testid="sales-payment-filter"><SelectValue placeholder="Payment" /></SelectTrigger>
            <SelectContent>
              {["All", "Cash", "Card", "EFT", "Other", "Split"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="sales-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              {["All", "completed", "voided"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="col-span-2 md:col-span-1 flex gap-2 min-w-0">
            <Input type="date" className="min-w-0" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="sales-date-from" />
            <Input type="date" className="min-w-0" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="sales-date-to" />
          </div>
        </div>
      </Card>

      <Card className="border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto thin-scroll">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Receipt</TableHead>
                <TableHead>Cashier</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Lines</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(s => (
                <TableRow key={s.id} className="hover:bg-slate-50" data-testid={`sale-row-${s.receipt_no}`}>
                  <TableCell className="text-xs text-muted-foreground">{new Date(s.at).toLocaleString("en-ZA")}</TableCell>
                  <TableCell className="font-mono text-xs">{s.receipt_no}</TableCell>
                  <TableCell className="text-sm">{s.cashier_name}</TableCell>
                  <TableCell className="text-xs" data-testid={`sale-payment-${s.receipt_no}`}>
                    {s.payment_method === "Split" && s.payment_breakdown ? (
                      <span title={`Cash ${currency(s.payment_breakdown.cash)} · Card ${currency(s.payment_breakdown.card)}`}>
                        Split — Cash {currency(s.payment_breakdown.cash)} / Card {currency(s.payment_breakdown.card)}
                      </span>
                    ) : (
                      s.payment_method
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="flex items-center gap-1.5">
                      <span>{s.lines.length}</span>
                      {s.sale_note && (
                        <Badge
                          variant="outline"
                          className="h-4 px-1.5 text-[9px] uppercase tracking-widest border-primary/30 text-primary bg-primary/5"
                          title={s.sale_note}
                          data-testid={`sale-note-flag-${s.receipt_no}`}
                        >
                          Note
                        </Badge>
                      )}
                      {s.manual_discount_type && (
                        <Badge
                          variant="outline"
                          className="h-4 px-1.5 text-[9px] uppercase tracking-widest border-secondary/40 text-secondary bg-secondary/5"
                          title={`Manual discount ${s.manual_discount_type === "percent" ? s.manual_discount_value + "%" : "R" + Number(s.manual_discount_value).toFixed(2)} · ${s.manual_discount_reason || ""}`}
                          data-testid={`sale-discount-flag-${s.receipt_no}`}
                        >
                          Discount
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">{currency(s.total)}</TableCell>
                  <TableCell>
                    <Badge className={s.status === "voided"
                      ? "bg-red-100 text-red-800 hover:bg-red-100"
                      : "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"}>
                      {s.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => openDetail(s)} data-testid={`view-sale-${s.receipt_no}`}>
                      <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                    {canModify && s.status === "completed" && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-sale-${s.receipt_no}`}>
                          <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-[#C05746]" onClick={() => openVoid(s)} data-testid={`void-sale-${s.receipt_no}`}>
                          <Ban className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No sales match those filters.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Sale detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className={`${isMgmt ? "sm:max-w-2xl" : "sm:max-w-lg"} max-h-[90vh] overflow-y-auto`} data-testid="sale-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Sale details</DialogTitle>
            <DialogDescription className="font-mono">{detail?.receipt_no}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3">
              {isMgmt ? <SaleTransactionDetail saleId={detail.id} /> : (<>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <Info label="Cashier" value={detail.cashier_name} />
                <Info label="Payment" value={detail.payment_method} />
                <Info label="When" value={new Date(detail.at).toLocaleString("en-ZA")} />
                <Info label="Status" value={detail.status} />
                {detail.edited_at && <Info label="Edited" value={new Date(detail.edited_at).toLocaleString("en-ZA")} />}
                {detail.voided_at && <Info label="Voided" value={new Date(detail.voided_at).toLocaleString("en-ZA")} />}
              </div>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.lines.map((l, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <div className="text-sm">{l.name}</div>
                          <div className="font-mono text-[10px] text-muted-foreground">{l.sku}{l.special_deal ? ` · ${l.special_deal}` : ""}</div>
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">{l.quantity}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{currency(l.unit_price)}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{currency(l.line_total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex justify-between font-display text-xl">
                <span>Total</span>
                <span>{currency(detail.total)}</span>
              </div>
              </>)}
              {detail.manual_discount_type && (
                <div className="rounded-md border border-secondary/30 bg-secondary/5 p-3 space-y-1" data-testid="sale-detail-manual-discount">
                  <div className="text-[10px] uppercase tracking-widest text-secondary mb-1">Manual discount</div>
                  <div className="flex justify-between text-sm font-mono">
                    <span>Subtotal after deal</span><span>{currency(detail.pre_manual_discount_total)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-mono">
                    <span>
                      {detail.manual_discount_type === "percent"
                        ? `Discount (${detail.manual_discount_value}%)`
                        : `Discount (R${Number(detail.manual_discount_value).toFixed(2)} fixed)`}
                    </span>
                    <span>− {currency(detail.manual_discount_amount)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-mono font-semibold pt-1">
                    <span>Final total</span><span>{currency(detail.total)}</span>
                  </div>
                  {detail.manual_discount_reason && (
                    <div className="text-xs text-muted-foreground pt-1">Reason: {detail.manual_discount_reason}</div>
                  )}
                  {detail.manual_discount_requested_by && (
                    <div className="text-[11px] text-muted-foreground">
                      Requested by {detail.manual_discount_requested_by.name || "—"}
                    </div>
                  )}
                  {detail.manual_discount_approved_by && (
                    <div className="text-[11px] text-emerald-700">
                      Approved by {detail.manual_discount_approved_by.name || "—"}
                      {detail.manual_discount_approved_by.role && ` (${detail.manual_discount_approved_by.role.replace("_", " ")})`}
                    </div>
                  )}
                </div>
              )}
              {detail.payment_method === "Split" && detail.payment_breakdown && (
                <div className="rounded-md border border-primary/20 bg-primary/5 p-3" data-testid="sale-detail-split">
                  <div className="text-[10px] uppercase tracking-widest text-primary mb-1">Split payment</div>
                  <div className="flex justify-between text-sm font-mono">
                    <span>Cash</span><span>{currency(detail.payment_breakdown.cash)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-mono">
                    <span>Card</span><span>{currency(detail.payment_breakdown.card)}</span>
                  </div>
                </div>
              )}
              {detail.sale_note && (
                <div className="rounded-md border border-primary/20 bg-primary/5 p-3" data-testid="sale-detail-note">
                  <div className="text-[10px] uppercase tracking-widest text-primary mb-1">Sale Note</div>
                  <div className="text-sm whitespace-pre-wrap">{detail.sale_note}</div>
                </div>
              )}
              {detail.deal_id && (
                <div className="rounded-md bg-primary/5 border border-primary/20 p-3 text-xs" data-testid="sale-detail-deal">
                  <div className="flex items-center gap-2 text-primary font-medium">
                    <span>Deal applied: {detail.deal_name}</span>
                  </div>
                  <div className="mt-1 flex justify-between text-muted-foreground">
                    <span>Original</span><span className="font-mono">{currency(detail.original_total)}</span>
                  </div>
                  <div className="flex justify-between text-secondary">
                    <span>Discount</span><span className="font-mono">− {currency(detail.discount_amount)}</span>
                  </div>
                  <div className="flex justify-between font-semibold">
                    <span>Final</span><span className="font-mono">{currency(detail.total)}</span>
                  </div>
                </div>
              )}
              <div>
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
                  <History className="w-3 h-3" strokeWidth={1.5} /> Audit trail
                </div>
                {audit.length === 0 ? (
                  <div className="text-xs text-muted-foreground">No audit entries.</div>
                ) : (
                  <div className="space-y-1">
                    {audit.map(a => (
                      <div key={a.id} className="text-xs flex justify-between border-b py-1">
                        <div>
                          <span className="font-medium">{a.action}</span> <span className="text-muted-foreground">by {a.actor_email}</span>
                          {a.meta?.reason && <span className="text-muted-foreground"> — “{a.meta.reason}”</span>}
                        </div>
                        <div className="text-muted-foreground">{new Date(a.at).toLocaleString("en-ZA")}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Void dialog */}
      <Dialog open={!!voiding} onOpenChange={(o) => !o && setVoiding(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Void sale</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">Receipt {voiding?.receipt_no} · {currency(voiding?.total)}</div>
          <Input placeholder="Reason (audit)" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} data-testid="void-reason" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setVoiding(null)}>Cancel</Button>
            <Button className="rounded-full bg-[#C05746] hover:bg-[#a94a3d]" onClick={submitVoid} data-testid="submit-void-btn">Void</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className={`${isMgmt ? "sm:max-w-2xl" : "sm:max-w-lg"} max-h-[90vh] overflow-y-auto`} data-testid="sale-detail-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Edit sale</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground mb-2">{editing?.receipt_no}</div>
          <div className="space-y-2 max-h-64 overflow-y-auto thin-scroll">
            {editLines.map((l, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`edit-line-${l.sku}`}>
                <div className="flex-1 text-sm truncate">
                  <div>{l.name}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">{l.sku}</div>
                </div>
                <Input type="number" step="0.01" className="w-24 text-right font-mono"
                       value={l.quantity}
                       onChange={(e) => {
                         const nv = [...editLines]; nv[i] = { ...nv[i], quantity: e.target.value };
                         setEditLines(nv);
                       }} />
              </div>
            ))}
          </div>
          <Input className="mt-2" placeholder="Reason (audit)" value={editReason} onChange={(e) => setEditReason(e.target.value)} data-testid="edit-reason" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="rounded-full bg-primary hover:bg-primary/90" onClick={submitEdit} data-testid="submit-edit-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
